import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";

import { Grid, IconButton, Divider, Button, Fab } from "@material-ui/core";
import { CatalogsIcon, MoreVertIcon, AddIcon, ExportIcon } from "../components/Icons";

import { listCatalogs, deleteCatalog, exportValues } from "../api/CatalogAPI";
import { AppContext } from "../context/AppContext";
import { Catalog, Catalogs, CatalogsQueryParams, ExportCatalogValues } from "../model/Catalog";

import translate from "../i18n/Translator";
import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import ValidatedInput from "../components/ValidatedInput";
import Gridable from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import DialogPopup from "../components/DialogPopup";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import CustomBackdrop from "../components/CustomBackdrop";
import CatalogMenu from "./CatalogMenu";
import DownloadExportationPopup from "../components/DownloadExportationPopup";
import ConnectorObjectExporter from "../connectors/ConnectorObjectExporter";
import { Family } from "../model/Connector";
import ConnectorTemplateDownloader from "../connectors/ConnectorTemplateDownloader";

type Popup = "delete" | "export" | "template";

export default function CatalogsList() {
    const history = useHistory();
    const context = useContext(AppContext);
    const tenantId = context.session?.tenant?.id || "-";

    const query = new URLSearchParams(useLocation().search);
    const search = query.get("search");

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Catalogs>();
    const [workingParams, setWorkingParams] = useState<CatalogsQueryParams>({
        search: search,
    } as CatalogsQueryParams);

    const [catalog, setCatalog] = useState<Catalog>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [popup, setPopup] = useState<Popup>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    const [page, setPage] = useState<number>(initialPage(query));
    const [pageSize, setPageSize] = useState<number>(initialPageSize(query));
    const [isCreateGranted] = useState(context.isGranted("CatalogsCreate"));
    const [exportResult, setExportResult] = useState<ExportCatalogValues>();

    const load = () => {
        const offset = getOffset(page, pageSize);
        const params = {
            search: search || "",
        } as CatalogsQueryParams;
        setWorkingParams(params);

        listCatalogs(tenantId, pageSize, offset, params).then((response) => {
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    useEffect(load, [tenantId, page, pageSize, search]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        query.set("search", workingParams.search);
        query.set("page", "1");
        history.push(`${window.location.pathname}?${query.toString()}`);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedOptions = (catalog: Catalog) => (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setCatalog(catalog);
    };

    const onClosePopup = () => {
        onCloseOption();
        setPopup(undefined);
    };

    const onDelete = () => {
        if (!catalog) return;

        setAnchorEl(null);
        setPopup("delete");
    };

    const onConfirmDelete = () => {
        onClosePopup();
        if (!catalog) return;

        setSubmitting(true);
        deleteCatalog(tenantId, catalog.id).then(() => {
            setSuccess(
                translate("catalogs.delete.success", { id: catalog.id }) as string
            );
            load();
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onExport = () => {
        setPopup("export");
        setAnchorEl(null);

    };

    const onCloseExporter = () => {
        setPopup(undefined);
        setCatalog(undefined);
    };

    const confirmExport = (connectorId: string) => {
        if (!catalog) return;

        setPopup(undefined);
        setSubmitting(true);
        exportValues(tenantId, catalog.id, connectorId).then(response => {
            if (response.url) {
                setExportResult(response);
            } else {
                setSuccess(translate("providers.email_export", { "total": response.total }) as string);
            }
            setCatalog(undefined);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onCloseExport = () => {
        setExportResult(undefined);
    };

    const onClosedSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setCatalog(undefined);
    };

    const onClickRow = (catalog: Catalog) => {
        history.push(`/catalogs/${catalog.id}/edit`);
    };

    return (
        <Pagination title={translate("catalogs.title")} icon={<CatalogsIcon />}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize} action={(
                <Grid container spacing={1} justify="center" alignContent="center" alignItems="center">
                    {isCreateGranted && (
                        <Grid item xs="auto">
                            <Link to="/catalogs/new">
                                <Fab color="primary" size="small" title={translate("buttons.add") as string}>
                                    <AddIcon />
                                </Fab>
                            </Link>
                        </Grid>
                    )}
                    <Grid item xs="auto">
                        <IconButton size="small" color="default" title={translate("connectors.template_downloader.title") as string} onClick={() => setPopup("template")}>
                            <ExportIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            )}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} sm>
                        <ValidatedInput type="text" id="search" name="search" label={translate("catalogs.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("catalogs.empty") as string}
                onClick={onClickRow}
                columns={[
                    {
                        title: translate("catalogs.name") as string,
                        converter: (catalog) => (
                            <Ellipsis text={catalog.name} lenght={100} uppercased={false} />
                        ),
                        xs: true
                    },
                    {
                        title: translate("catalogs.external_id") as string,
                        converter: (catalog) => (
                            <Ellipsis text={catalog.external_id ?? ""} lenght={100} uppercased={false} />
                        ),
                        xs: true
                    },
                    {
                        title: translate("catalogs.values") as string,
                        converter: (catalog) => catalog.values?.filter(value => !value.deleted_in_use).length ?? 0,
                        xs: 4,
                        sm: 4,
                        md: 4,
                        lg: 3,
                        xl: 2
                    },
                    {
                        title: (
                            <IconButton size="small" style={{ "visibility": "hidden" }} disabled>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        converter: (catalog) => (
                            <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(catalog)}>
                                <MoreVertIcon />
                            </IconButton>
                        ),
                        xs: "auto"
                    }
                ]} />
            {catalog && anchorEl &&
                <CatalogMenu
                    catalog={catalog}
                    anchor={anchorEl}
                    onDelete={onDelete}
                    onExport={onExport}
                    onClose={onCloseOption} />
            }
            {catalog && popup === "delete" && (
                <DialogPopup
                    open
                    title={translate("catalogs.delete.title")}
                    onClose={onClosePopup}
                    closeColor="default"
                    disableEscapeKeyDown={submitting}
                    disableBackdropClick={submitting}
                    disable={submitting}
                    button={
                        <Button
                            onClick={onConfirmDelete}
                            variant="outlined"
                            color="secondary"
                            disabled={submitting}
                        >
                            {translate("buttons.delete")}
                        </Button>
                    }
                >
                    {translate("catalogs.delete.text", { id: catalog.id })}
                </DialogPopup>
            )}
            {catalog && popup === "export" && (
                <ConnectorObjectExporter
                    tenantId={tenantId}
                    family={Family.CATALOGS}
                    type="DATA"
                    onExport={(connectorId: string) => confirmExport(connectorId)}
                    onClose={onCloseExporter} />
            )}
            {popup === "template" && (
                <ConnectorTemplateDownloader
                    tenantId={tenantId}
                    family={Family.CATALOGS}
                    onClose={onCloseExporter} />
            )}
            {exportResult && exportResult.url && (
                <DownloadExportationPopup
                    title={translate("catalogs.export.title") as string}
                    message={translate("catalogs.export.message", { "total": exportResult.total }) as string}
                    url={exportResult.url}
                    onClose={onCloseExport} />
            )}
            <SuccessSnackbar message={success} onClose={onClosedSnackbars} />
            <ErrorSnackbar message={error} onClose={onClosedSnackbars} />
            <CustomBackdrop open={submitting} />
        </Pagination>
    );
}